import React, { useState } from 'react';

import Button from '../Button';
import FormGroup from '../FormGroup';
import Label from '../Label';
import FormControlMessage from '../FormControlMessage';
import FilesList from './FilesList';
import Upload from './Upload';
import withTheme from '../../hocs/withTheme';

import type {
    UploadFile,
    UploadChangeEvent,
} from './Upload/core/ImplicitFileInput';
import { BUTTON_TYPES } from '../Button/constants';

interface FileInputProps {
    uploadText?: string;
    label?: string | null;
    labelInfobox?: boolean;
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    error?: string;
    accept?: string;
    children?: React.ReactNode;
    defaultFileList?: UploadFile[];
    onChange?: (files: UploadFile[]) => void;
    showFileList?: boolean;
    buttonType?: (typeof BUTTON_TYPES)[number];
}

export const FileInput: React.FC<FileInputProps> = ({
    uploadText = 'Browse',
    label = null,
    labelInfobox = false,
    disabled = false,
    required = false,
    multiple = false,
    error = '',
    accept = '*',
    children = null,
    defaultFileList = [],
    onChange = () => {},
    showFileList = true,
    buttonType = 'secondary',
    ...rest
}) => {
    const [files, setFiles] =
        useState<UploadFile[]>(defaultFileList);

    const beforeUpload = (file: UploadFile) => {
        const newFilesList = !multiple
            ? [file]
            : [...files, file];

        setFiles(newFilesList);
        onChange(newFilesList);

        return false;
    };

    const handleChange = ({
        file,
        fileList,
    }: UploadChangeEvent) => {
        const changedFiles = multiple
            ? fileList
            : fileList.filter(
                  (fileItem) => fileItem.uid === file.uid
              );

        setFiles(changedFiles);
        onChange(changedFiles);
    };

    return (
        <FormGroup disabled={disabled} error={error}>
            {label && (
                <Label
                    htmlFor={label}
                    label={label}
                    labelInfobox={labelInfobox}
                    required={required}
                />
            )}

            {showFileList && (
                <FilesList
                    files={files}
                    onRemove={handleChange}
                />
            )}

            <Upload
                {...rest}
                files={files}
                onChange={handleChange}
                beforeUpload={beforeUpload}
                disabled={disabled}
                multiple={multiple}
            >
                <Button
                    size="small"
                    type={buttonType}
                    disabled={disabled}
                >
                    {uploadText}
                </Button>
            </Upload>

            {error && (
                <FormControlMessage>
                    {error}
                </FormControlMessage>
            )}

            {children && <p>{children}</p>}
        </FormGroup>
    );
};

export default withTheme(FileInput);
